import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import { customersAPI } from '../services/api';
import { Edit, X } from 'lucide-react';
import { toast } from 'sonner';
import { displayPhoneNumber } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import MapsLink from '../components/MapsLink';

const Customers = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, customerId: null });
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [globalSearch, setGlobalSearch] = useState('');
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState({
    type: '', number: '', company: '', lastName: '',
    firstName: '', primaryPhone: '', alternatePhone: '', email: '', address: ''
  });
  const [missingContactFilter, setMissingContactFilter] = useState(false);

  // Count customers missing phone & email (legacy/manual imports without contact info)
  const missingContactCount = customers.filter(c => !c.primary_phone && !c.alternate_phone && !c.email).length;

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await customersAPI.getAll();
      setCustomers(data || []);
    } catch {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const getAddress = (c) => {
    if (!c.addresses || c.addresses.length === 0) return '';
    const addr = c.addresses.find(a => a.current) || c.addresses[0];
    return `${addr.street_address || ''} ${addr.city || ''} ${addr.state || ''} ${addr.zip || ''}`.trim();
  };

  const filtered = customers.filter(c => {
    const g = globalSearch.toLowerCase();
    const matchesGlobal = !g || [
      c.type, c.customer_id, c.company, c.primary_last_name, c.primary_first_name,
      c.primary_phone, c.alternate_phone, c.email, getAddress(c)
    ].some(v => (v || '').toLowerCase().includes(g));

    const f = columnFilters;
    const matchesCol =
      (!f.type || (c.type || '').toLowerCase().includes(f.type.toLowerCase())) &&
      (!f.number || (c.customer_id || '').toLowerCase().includes(f.number.toLowerCase())) &&
      (!f.company || (c.company || '').toLowerCase().includes(f.company.toLowerCase())) &&
      (!f.lastName || (c.primary_last_name || '').toLowerCase().includes(f.lastName.toLowerCase())) &&
      (!f.firstName || (c.primary_first_name || '').toLowerCase().includes(f.firstName.toLowerCase())) &&
      (!f.primaryPhone || (c.primary_phone || '').includes(f.primaryPhone)) &&
      (!f.alternatePhone || (c.alternate_phone || '').includes(f.alternatePhone)) &&
      (!f.email || (c.email || '').toLowerCase().includes(f.email.toLowerCase())) &&
      (!f.address || getAddress(c).toLowerCase().includes(f.address.toLowerCase()));

    return matchesGlobal && matchesCol;
  });

  const formatCreatedOn = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const toggleCreatedSort = () => setSort(prev => (
    prev.key === 'created_at'
      ? { key: 'created_at', direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key: 'created_at', direction: 'desc' }
  ));

  const sorted = sort.key === 'created_at'
    ? [...filtered].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return sort.direction === 'asc' ? ta - tb : tb - ta;
      })
    : filtered;

  const displayed = sorted.slice(0, entriesPerPage);

  const handleRowClick = (customerId) => navigate(`/customers/${customerId}`);
  const handleEditClick = (e, customerId) => { e.stopPropagation(); navigate(`/customers/${customerId}`); };
  const openDeleteDialog = (e, customerId) => { e.stopPropagation(); e.preventDefault(); setDeleteDialog({ isOpen: true, customerId }); };
  const handleConfirmDelete = async () => {
    const customerId = deleteDialog.customerId;
    setDeleteDialog({ isOpen: false, customerId: null });
    try {
      await customersAPI.delete(customerId);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch {
      toast.error('Failed to delete customer');
    }
  };
  const handleCancelDelete = () => setDeleteDialog({ isOpen: false, customerId: null });

  const updateFilter = (key, value) => setColumnFilters(prev => ({ ...prev, [key]: value }));

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl lg:text-3xl text-[#5B8DB8] font-light mb-1">Customers</h2>
            <h1 className="text-3xl lg:text-4xl text-[#5B8DB8] font-light">All Customers</h1>
          </div>
          <button
            onClick={() => navigate('/customers/new')}
            className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-5 py-2 rounded transition-colors text-sm"
            data-testid="add-customer-btn"
          >
            + Add Customer
          </button>
        </div>

        <div className="bg-white rounded shadow p-4 lg:p-6">
          {!loading && missingContactCount > 0 && (
            <div
              className={`mb-4 rounded border-l-4 px-4 py-3 flex items-center justify-between gap-3 ${missingContactFilter ? 'border-amber-500 bg-amber-50' : 'border-amber-400 bg-amber-50/60'}`}
              data-testid="missing-contact-banner"
            >
              <div className="text-sm text-amber-900">
                <strong>{missingContactCount}</strong> {missingContactCount === 1 ? 'customer is' : 'customers are'} missing phone &amp; email.
              </div>
              <button
                onClick={() => setMissingContactFilter(v => !v)}
                className={`text-xs font-medium px-3 py-1 rounded-full border transition-colors ${missingContactFilter ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-400 hover:bg-amber-100'}`}
                data-testid="missing-contact-filter-btn"
              >
                {missingContactFilter ? 'Showing only missing — click to clear' : 'Show only these'}
              </button>
            </div>
          )}
          {/* Top controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              Show
              <select value={entriesPerPage} onChange={(e) => setEntriesPerPage(Number(e.target.value))} className="border border-gray-300 rounded px-2 py-1">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              entries
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              Search:
              <input
                type="text"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1"
                data-testid="global-search"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading customers...</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left p-3 text-gray-600 font-normal italic">Type</th>
                    <th className="text-left p-3 text-gray-600 font-normal italic">Number</th>
                    <th className="text-left p-3 text-gray-600 font-normal italic">Company</th>
                    <th className="text-left p-3 text-gray-600 font-normal italic">Last Name</th>
                    <th className="text-left p-3 text-gray-600 font-normal italic">First Name</th>
                    <th className="text-left p-3 text-gray-600 font-normal italic">Primary Phone</th>
                    <th className="text-left p-3 text-gray-600 font-normal italic">Alternate Phone</th>
                    <th className="text-left p-3 text-gray-600 font-normal italic">Email</th>
                    <th className="text-left p-3 text-gray-600 font-normal italic">Current Address</th>
                    <th
                      className="text-left p-3 text-gray-600 font-normal italic cursor-pointer select-none whitespace-nowrap hover:text-[#5B8DB8]"
                      onClick={toggleCreatedSort}
                      data-testid="sort-created-on"
                      title="Sort by date added"
                    >
                      Created On{sort.key === 'created_at' ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                    </th>
                    <th className="p-3"></th>
                  </tr>
                  {/* Column filter row */}
                  <tr className="border-b">
                    <td className="p-2"><input type="text" value={columnFilters.type} onChange={(e) => updateFilter('type', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"><input type="text" value={columnFilters.number} onChange={(e) => updateFilter('number', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"><input type="text" value={columnFilters.company} onChange={(e) => updateFilter('company', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"><input type="text" value={columnFilters.lastName} onChange={(e) => updateFilter('lastName', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"><input type="text" value={columnFilters.firstName} onChange={(e) => updateFilter('firstName', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"><input type="text" value={columnFilters.primaryPhone} onChange={(e) => updateFilter('primaryPhone', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"><input type="text" value={columnFilters.alternatePhone} onChange={(e) => updateFilter('alternatePhone', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"><input type="text" value={columnFilters.email} onChange={(e) => updateFilter('email', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"><input type="text" value={columnFilters.address} onChange={(e) => updateFilter('address', e.target.value)} className="border border-gray-300 rounded px-2 py-1 w-full text-xs" /></td>
                    <td className="p-2"></td>
                    <td className="p-2"></td>
                  </tr>
                </thead>
                <tbody>
                  {displayed.length === 0 ? (
                    <tr><td colSpan="11" className="text-center py-8 text-gray-500">No customers found</td></tr>
                  ) : (
                    displayed.map((customer, index) => (
                      <tr
                        key={customer._id || customer.customer_id}
                        className={`border-b hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        <td className="p-3 text-gray-700">{customer.type || 'Residential'}</td>
                        <td className="p-3 text-gray-700">{customer.customer_id}</td>
                        <td className="p-3 text-gray-700">{customer.company || ''}</td>
                        <td className="p-3 text-gray-700">{customer.primary_last_name || ''}</td>
                        <td className="p-3 text-gray-700">{customer.primary_first_name || ''}</td>
                        <td className="p-3 text-gray-700">{displayPhoneNumber(customer.primary_phone)}</td>
                        <td className="p-3 text-gray-700">{displayPhoneNumber(customer.alternate_phone)}</td>
                        <td className="p-3 text-gray-700">{customer.email || ''}</td>
                        <td className="p-3 text-xs">
                          <MapsLink address={getAddress(customer)} testid={`customer-address-link-${customer.customer_id}`} />
                        </td>
                        <td className="p-3 text-gray-700 whitespace-nowrap" data-testid={`customer-created-on-${customer.customer_id}`}>
                          {formatCreatedOn(customer.created_at)}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {hasPermission('can_update') && (
                            <Link
                              to={`/customers/${customer.customer_id}`}
                              data-testid={`edit-customer-${customer.customer_id}`}
                              className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5 inline-flex items-center justify-center"
                              onClick={(e) => e.stopPropagation()}
                              title="Edit customer (right-click to open in new tab)"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Link>
                            )}
                            {hasPermission('can_delete') && (
                            <button
                              data-testid={`delete-customer-${customer.customer_id}`}
                              className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1.5"
                              onClick={(e) => openDeleteDialog(e, customer.customer_id)}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Bottom info */}
          <div className="mt-4 text-sm text-gray-600">
            Showing 1 to {displayed.length} of {filtered.length} entries
            {globalSearch && ` (filtered from ${customers.length} total)`}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title="Delete Customer"
        message="Are you sure you want to delete this customer?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Layout>
  );
};

export default Customers;
